import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { DNI_CONFIG, type DNIContext } from '@/lib/dni/config';
import { 
  initDNIRouter, 
  loadDNIContext, 
  formatPhoneDisplay, 
  getTelLink,
  swapPhoneNumbers,
  getDefaultPhoneForPath,
} from '@/lib/dni/numberRouter';

/**
 * Hook to access DNI context and phone numbers
 */
export const useDNI = () => {
  const [context, setContext] = useState<DNIContext | null>(null);
  const [isReady, setIsReady] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Check for stored context first
    const stored = loadDNIContext();
    if (stored) {
      setContext(stored);
    }
    
    // Initialize the router
    const newContext = initDNIRouter();
    setContext(newContext);
    setIsReady(true);
    
    // If using Twilio router, swap after a short delay
    if (!DNI_CONFIG.USE_CALLRAIL && DNI_CONFIG.USE_TWILIO_ROUTER) {
      const timer = setTimeout(() => {
        swapPhoneNumbers(newContext);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  // Get location-aware default phone
  const defaultPhone = getDefaultPhoneForPath(location.pathname);

  const getDisplayNumber = useCallback((format: 'formatted' | 'raw' = 'raw'): string => {
    const number = context?.assignedNumber || defaultPhone;
    return formatPhoneDisplay(number, format);
  }, [context, defaultPhone]);

  const getTelHref = useCallback((): string => {
    const number = context?.assignedNumber || defaultPhone;
    return getTelLink(number);
  }, [context, defaultPhone]);

  return {
    context,
    isReady,
    assignedNumber: context?.assignedNumber || defaultPhone,
    getDisplayNumber,
    getTelHref,
    city: context?.city,
    campaign: context?.campaign,
    source: context?.source,
    gclid: context?.gclid,
  };
};

/**
 * Get DNI form fields for hidden inputs
 */
export const useDNIFormFields = () => {
  const { context, assignedNumber, city, campaign, source, gclid } = useDNI();
  
  return {
    assigned_tracking_number: assignedNumber,
    city_detected: city || '',
    utm_source: context?.source || '',
    utm_medium: context?.medium || '',
    utm_campaign: context?.campaign || '',
    gclid: context?.gclid || '',
    landing_page: context?.landingPage || '',
  };
};
